// 1. forwardRef와 Ref 타입을 React에서 import 합니다.
import React, { Dispatch, Ref, SetStateAction, forwardRef, useEffect, useState } from "react";
import styled from "styled-components";

import { initialProductData } from "@src/hooks/stateLand/useStateLand";

interface SubscriptionContentsProps {
    id: string;
    list: any;
    selectedProductInfo: any;
    setSelectedProductInfo: Dispatch<SetStateAction<any>>;
    title: string;
    primaryColor: string;
}

const StandardProductIos = forwardRef(
    (
        { id, list, selectedProductInfo, setSelectedProductInfo, title, primaryColor }: SubscriptionContentsProps,
        scrollRef: Ref<HTMLDivElement>,
    ) => {
        const yearlyProduct = list.filter((item: any) => item.name.includes("1년 구독권"));
        const monthlyProduct = list.filter((item: any) => item.name.includes("월 정기구독"));
        const monthlyTrialProduct = list.filter((item: any) => item.name.includes("1개월 체험"));

        return (
            <Container ref={scrollRef} id={id}>
                <DescriptionBox>
                    <SubsTitle $primaryColor={primaryColor}>
                        {title}
                        <YearlyProductDescription $primaryColor={primaryColor}>
                            <div className="텍스트">{yearlyProduct[0]?.name}</div>
                        </YearlyProductDescription>
                    </SubsTitle>

                    <Description
                        dangerouslySetInnerHTML={{
                            __html:
                                yearlyProduct[0] &&
                                (yearlyProduct[0]?.displayDescription
                                    .replaceAll("background-color: #ffffff;", "")
                                    .replaceAll("background-color", "padding: 0px 4px; background-color") as string),
                        }}
                    />
                </DescriptionBox>

                <ProductBox>
                    {/* 년간 결제 */}
                    <YearlyProduct>
                        <ProductTitle>연 구독권</ProductTitle>

                        <ProductItem
                            $selected={selectedProductInfo?.no === yearlyProduct[0]?.no}
                            $primaryColor={primaryColor}
                            onClick={() => {
                                if (selectedProductInfo?.no === yearlyProduct[0]?.no) {
                                    setSelectedProductInfo(initialProductData);
                                } else {
                                    setSelectedProductInfo(yearlyProduct[0]);
                                }
                            }}
                        >
                            <Pricebox>
                                <Price>
                                    <div className="기존가격">정가 {yearlyProduct[0]?.price?.toLocaleString()}원</div>
                                    <div className="결제가격">
                                        연 {yearlyProduct[0]?.term1DiscountedPrice?.toLocaleString()}원
                                    </div>
                                    <div className="나눈가격">
                                        (환산 시 월{" "}
                                        {(Math.floor(yearlyProduct[0]?.term1DiscountedPrice / 12 / 1000) * 1000).toLocaleString()}
                                        원)
                                    </div>
                                </Price>

                                <PriceInfoBox>
                                    <PriceInfo>
                                        <div className="좌측">결제방식</div>
                                        <div className="우측">매년 결제</div>
                                    </PriceInfo>
                                </PriceInfoBox>
                            </Pricebox>

                            <div className="유혹문구">가장 합리적인 가격으로 시작해보세요.</div>
                        </ProductItem>
                    </YearlyProduct>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        {/* 월 결제 */}
                        <MonthlyProduct>
                            <ProductTitle>월 구독권</ProductTitle>

                            <ProductItem
                                $selected={selectedProductInfo?.no === monthlyProduct[0]?.no}
                                $primaryColor={primaryColor}
                                onClick={() => {
                                    if (selectedProductInfo?.no === monthlyProduct[0]?.no) {
                                        setSelectedProductInfo(initialProductData);
                                    } else {
                                        setSelectedProductInfo(monthlyProduct[0]);
                                    }
                                }}
                            >
                                <Pricebox>
                                    <Price>
                                        <div className="기존가격">정가 {monthlyProduct[0]?.price?.toLocaleString()}원</div>
                                        <div className="결제가격">
                                            월 {monthlyProduct[0]?.term1DiscountedPrice?.toLocaleString()}원
                                        </div>
                                    </Price>

                                    <PriceInfoBox>
                                        <PriceInfo>
                                            <div className="좌측">결제방식</div>
                                            <div className="우측">매월 결제</div>
                                        </PriceInfo>
                                    </PriceInfoBox>
                                </Pricebox>

                                <div className="유혹문구">
                                    큰 금액 결제가 <br />
                                    부담되신 분께 추천드려요!
                                </div>
                            </ProductItem>
                        </MonthlyProduct>

                        {/* 1개월 체험 */}
                        <MonthlyProduct>
                            <ProductTitle>1개월권</ProductTitle>

                            <ProductItem
                                $selected={selectedProductInfo?.no === monthlyTrialProduct[0]?.no}
                                $primaryColor={primaryColor}
                                onClick={() => {
                                    if (selectedProductInfo?.no === monthlyTrialProduct[0]?.no) {
                                        setSelectedProductInfo(initialProductData);
                                    } else {
                                        setSelectedProductInfo(monthlyTrialProduct[0]);
                                    }
                                }}
                            >
                                <Pricebox>
                                    <Price>
                                        <div className="기존가격">정가 {monthlyTrialProduct[0]?.price?.toLocaleString()}원</div>
                                        <div className="결제가격">
                                            {monthlyTrialProduct[0]?.term1DiscountedPrice?.toLocaleString()}원
                                        </div>
                                    </Price>

                                    <PriceInfoBox>
                                        <PriceInfo>
                                            <div className="좌측">결제방식</div>
                                            <div className="우측">일시납</div>
                                        </PriceInfo>
                                    </PriceInfoBox>
                                </Pricebox>

                                <div className="유혹문구">
                                    미스고를 체험해보고 <br /> 싶은 분들께 추천해요!
                                </div>
                            </ProductItem>
                        </MonthlyProduct>
                    </div>
                </ProductBox>
            </Container>
        );
    },
);

StandardProductIos.displayName = "StandardProduct";

const Container = styled.div`
    box-sizing: border-box;
    padding: 20px 14px;

    display: flex;
    flex-direction: column;
    gap: 20px;

    scroll-margin-top: 400px;
`;

const DescriptionBox = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .commonImage {
        position: absolute;
        left: -109px;
    }
`;

const Description = styled.div`
    ul > li {
        margin: 0 0 8px 28px;
    }

    ul > li > span {
        background-color: none !important;

        text-align: center;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 26px;
    }
`;

const SubsTitle = styled.div<{ $primaryColor: string }>`
    color: ${({ $primaryColor }) => $primaryColor};
    font-size: 34px;
    font-style: normal;
    font-weight: 550;
    line-height: 62px;
`;

const PayButton = styled.button`
    width: 100%;
    padding: 14px 0;

    border-radius: 14px;
    background: #0047ff;

    color: #fff;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 40px;
`;

const ProductBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 40px;
`;

const YearlyProduct = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const MonthlyProduct = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const ProductTitle = styled.div`
    text-align: center;
    color: #282828;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 140%;
`;

const YearlyProductDescription = styled.div<{ $primaryColor: string }>`
    width: 100%;
    display: flex;
    flex-direction: row;
    white-space: pre-wrap;

    .텍스트 {
        color: ${({ $primaryColor }) => $primaryColor};
        font-size: 24px;
        font-style: normal;
        font-weight: 550;
        line-height: 30px;
    }
`;

const ProductItem = styled.div<{ $selected: boolean; $primaryColor: string }>`
    height: 266px;
    padding: 20px 12px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    cursor: pointer;

    border-radius: 14px;
    border: ${(props) => (props.$selected ? `2px solid ${props.$primaryColor}` : "2px solid #c9c9c9")};
    background: #fff;
    box-shadow: 2px 2px 6px 0 rgba(4, 0, 14, 0.14);

    .할인율 {
        width: fit-content;
        padding: 6px 10px;

        border-radius: 5px;
        background-color: ${(props) => props.$primaryColor};

        color: #fff;
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }

    .기존가격 {
        color: #a3a3a3;
        text-align: center;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
        text-decoration-line: line-through;
    }

    .결제가격 {
        color: #282828;
        font-size: 24px;
        font-style: normal;
        font-weight: 700;
        line-height: 140%;
    }

    .나눈가격 {
        color: #0c002c;
        font-size: 18px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
    }

    .유혹문구 {
        color: ${(props) => props.$primaryColor};
        text-align: center;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
    }
`;

const Pricebox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
`;

const Price = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const PriceInfoBox = styled.div`
    width: 100%;
`;

const PriceInfo = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 14px 0;

    border-top: 0.5px solid #e0e0e0;

    .좌측 {
        color: #515151;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
    }

    .우측 {
        color: #000;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 140%;
    }
`;

export default StandardProductIos;
